import React from "react";
import {Box, Button, Typography} from "@mui/material";

import PageHeader from "../components/Common/PageHeader";
import Splash from "./Splash";
import PersonaHeaderInfo from "../components/Common/PersonaHeaderInfo";
import PersonaScope from "../components/Reporting/PersonaScope";
import DashboardTile from "../components/Reporting/DashboardTile";
import useAppContext from "../util/AppContext";
import {hasPrivilege} from "../util/SessionContext";
import {Blue4, Grey6, Grey7} from "../theme/styles";
import {Dashboard, TileResult, getHomeDashboard, runDashboard}
        from "../components/Reporting/dashboardApi";
import EventBus from "../util/EventBus";
import {useNavigate} from "react-router-dom";

// Home.
//
// NO MARKETING LANDING. The clone came with t5's -- connectors, the glossary, quality rules, a form
// designer -- and shipping that on reports.cres-intelligence.io would advertise a product this
// domain does not serve, to anybody who found the name before there was anything to say about it.
// Deleted rather than adapted: writing marketing copy for an app that is four phases old is a way of
// promising things, and the honest pre-login page is a door.
//
// AFTER SIGNING IN IT SHOWS THE ACCOUNT'S HOME DASHBOARD, when one has been chosen -- which is the
// point of the flag: somebody arriving at this app should land on the numbers rather than on a
// door they have already come through. With none chosen it says what this app is and points at the
// one thing it does, which is what it always did.
export default function Home() {
    const sessionContext = useAppContext()?.sessionContext;
    const navigate = useNavigate();
    const signedIn = !!sessionContext?.valid();

    // ONLY FOR SOMEBODY WHO MAY USE REPORTING. The privilege is all-or-nothing and most of an
    // account does not hold it; asking for a dashboard on their behalf would return a refusal and
    // put an error on the first page they see. They get the door, as before.
    const mayReport = signedIn && hasPrivilege("reporting");

    const [dashboard, setDashboard] = React.useState<Dashboard | null>(null);
    const [tiles, setTiles] = React.useState<TileResult[]>([]);
    const [busy, setBusy] = React.useState(false);

    const load = React.useCallback(() => {
        if (!mayReport) return;
        setBusy(true);
        getHomeDashboard()
            .then(one => {
                setDashboard(one);
                if (!one) { setTiles([]); return; }
                // THE ARRANGEMENT FIRST, THEN THE NUMBERS. The headings are on screen while the
                // queries run, rather than a blank page that fills in all at once -- the same
                // order the dashboard page uses.
                setTiles((one.Tiles ?? []).map(tile => ({...tile})));
                return runDashboard(one.USID).then(setTiles);
            })
            // A HOME PAGE MUST NOT BE ABLE TO FAIL. If the dashboard has gone, or its reports will
            // not compile, the reader gets the door rather than an error where they expected their
            // numbers -- and every other way into the app still works.
            .catch(() => setDashboard(null))
            .finally(() => setBusy(false));
    }, [mayReport]);

    React.useEffect(() => { load(); }, [load]);

    // NO SWITCHER HERE ANY MORE. It was a dropdown on this one page, so the only way back to a
    // dashboard you had looked at yesterday was to come Home first -- and the list it opened was
    // invisible from every other screen in the app. Switching now lives in the rail, under Home,
    // where it is reachable from wherever you are and readable without opening anything; and
    // choosing which dashboard Home shows lives on the dashboard itself, where you can see what
    // you are choosing. This page shows the one that was resolved and nothing else.

    // THE NUMBERS BELONG TO WHOEVER IS LOOKING, here as everywhere else: changing persona re-runs
    // the tiles rather than leaving the previous persona's totals under the new one's name.
    React.useEffect(() => {
        const listener = () => load();
        // addListener/removeListener, as the dashboard page does -- this EventEmitter has those
        // and `off` is not one of them, which is a blank page rather than a warning.
        EventBus.visibility.addListener("change-visibility", listener);
        return () => { EventBus.visibility.removeListener("change-visibility", listener); };
    }, [load]);

    if (dashboard) {
        return (
            <div style={{display: "flex", flexDirection: "column", flexGrow: 1, minWidth: 0,
                         height: "100%", backgroundColor: "#FFFFFF"}}>
                <PageHeader title="Home">
                    <PersonaHeaderInfo/>
                </PageHeader>
                <div style={{flex: 1, overflow: "auto", padding: "20px 30px 40px"}}>
                    <div style={{display: "flex", flexDirection: "row", alignItems: "flex-end",
                                 justifyContent: "space-between", padding: "12px 0"}}>
                        <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                            <Typography style={{fontSize: 20, fontWeight: 500}}>{dashboard.Name}</Typography>
                        </Box>
                        <PersonaScope/>
                    </div>
                    {dashboard.Description ? (
                        <Typography sx={{fontSize: 12.5, color: Grey6, mb: 1.5}}>
                            {dashboard.Description}
                        </Typography>
                    ) : null}
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: "12px"}}>
                        {tiles.map(tile => (
                            <DashboardTile key={tile.USID ?? tile.ReportUSID} tile={tile}
                                           loading={busy && !tile.Columns && !tile.Refusal}/>
                        ))}
                    </Box>
                </div>
            </div>
        );
    }

    // SIGNED OUT, THE PAGE IS THE SPLASH. It was this same centred block with "Sign in to
    // continue." under it, which is a door and was meant to be one; what it never did was say what
    // is behind the door to somebody meeting this address for the first time. Splash.tsx carries
    // the reasoning about what that page may and may not claim.
    if (!signedIn) return <Splash/>;

    return (
        <div style={{display: "flex", flexDirection: "column", flexGrow: 1, height: "100%"}}>
            <PageHeader title="Home"/>
            <Box sx={{display: "flex", flexDirection: "column", alignItems: "center",
                      justifyContent: "center", flex: 1, width: "100%", textAlign: "center", px: 3}}>
                <Typography sx={{fontSize: {xs: 40, md: 56}, fontWeight: 700, color: Blue4,
                                 letterSpacing: "1px"}}>
                    Reporting
                </Typography>
                <Typography sx={{fontSize: 16, color: Grey7, mt: 1, maxWidth: "60ch"}}>
                    Query your own records — including the answers on your forms — and build
                    analytical views over them.
                </Typography>
                <Button sx={{mt: 3, textTransform: "none"}} variant="outlined"
                        onClick={() => navigate("/reporting")}>
                    Open the report builder
                </Button>
            </Box>
        </div>
    );
}
